import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Router } from '@angular/router';
import { UserService } from './user.service';
import { ionicSafeHtml } from 'src/app/utils/ionic-safe-html';
import {
  normalizeSubscriptionPlan,
  subscriptionPlanMeetsMinimum,
  subscriptionPlanName,
  SubscriptionPlanId
} from 'src/app/config/subscription-plans';

export interface AppAccessState {
  hasAccess: boolean;
  isSubscriber: boolean;
  isTrialActive: boolean;
  isSubscriptionExpired: boolean;
  subscriptionPlanName: string;
  trialDaysRemaining: number;
}

@Injectable({
  providedIn: 'root'
})
export class AccessControlService {
  private alertOpen = false;
  private cachedAccessState: AppAccessState | null = null;
  private cachedAt = 0;
  private pendingAccessState: Promise<AppAccessState> | null = null;
  private backgroundSyncPending: Promise<void> | null = null;

  constructor(
    private alertCtrl: AlertController,
    private router: Router,
    private userService: UserService
  ) {}

  async getAccessState(options: { forceRefresh?: boolean } = {}): Promise<AppAccessState> {
    const now = Date.now();

    if (!options.forceRefresh && this.pendingAccessState) {
      return this.pendingAccessState;
    }

    if (!options.forceRefresh && this.cachedAccessState) {
      const localUser = this.userService.user || await this.userService.loadUser();
      const access = this.buildAccessState(localUser);
      this.cachedAccessState = access;
      this.cachedAt = now;
      return access;
    }

    if (!options.forceRefresh) {
      const localUser = this.userService.user || await this.userService.loadUser();

      if (localUser?.trial_end_date) {
        const access = this.buildAccessState(localUser);
        this.cachedAccessState = access;
        this.cachedAt = now;
        return access;
      }
    }

    this.pendingAccessState = this.resolveAccessState(options.forceRefresh);

    try {
      const access = await this.pendingAccessState;
      this.cachedAccessState = access;
      this.cachedAt = Date.now();
      return access;
    } finally {
      this.pendingAccessState = null;
    }
  }

  invalidateCache() {
    this.cachedAccessState = null;
    this.cachedAt = 0;
    this.pendingAccessState = null;
    this.backgroundSyncPending = null;
  }

  updateAccessStateFromCurrentUser(): AppAccessState {
    const access = this.buildAccessState(this.userService.user);
    this.cachedAccessState = access;
    this.cachedAt = Date.now();
    this.pendingAccessState = null;
    return access;
  }

  private async resolveAccessState(forceRefresh: boolean = false): Promise<AppAccessState> {
    let user = this.userService.user || await this.userService.loadUser();

    if (!user?.trial_end_date) {
      await this.userService.initTrialPeriod();
      user = this.userService.user || await this.userService.loadUser();
    } else if (forceRefresh) {
      await this.userService.syncUserStatus();
      user = this.userService.user || await this.userService.loadUser();
    }

    return this.buildAccessState(user);
  }

  private syncUserStatusInBackground(): void {
    if (this.backgroundSyncPending) {
      return;
    }

    this.backgroundSyncPending = this.userService.syncUserStatus()
      .then(() => {
        const access = this.buildAccessState(this.userService.user);
        this.cachedAccessState = access;
        this.cachedAt = Date.now();
      })
      .catch(() => {
        // A sincronização em segundo plano não deve travar a navegação.
      })
      .finally(() => {
        this.backgroundSyncPending = null;
      });
  }

  private buildAccessState(user: any): AppAccessState {
    const isSubscriber = this.isPaidSubscriptionActive(user);
    const trialDaysRemaining = this.getTrialDaysRemaining(user?.trial_end_date);
    const plan = normalizeSubscriptionPlan(user?.subscription_plan);
    const isSubscriptionExpired = !isSubscriber && !!plan && plan !== 'trial';
    const isTrialActive = !isSubscriber && !isSubscriptionExpired && trialDaysRemaining > 0;

    return {
      hasAccess: isSubscriber || isTrialActive,
      isSubscriber,
      isTrialActive,
      isSubscriptionExpired,
      subscriptionPlanName: isSubscriptionExpired || isSubscriber
        ? subscriptionPlanName(plan)
        : '',
      trialDaysRemaining
    };
  }

  private isPaidSubscriptionActive(user: any): boolean {
    if (user?.modo_dev === true) {
      return true;
    }

    if (Number(user?.user_paid || 0) < 1) {
      return false;
    }

    const paidEndDate = this.parsePaidEndDate(user);
    return !!paidEndDate && paidEndDate.getTime() >= Date.now();
  }

  async canUseFeature(featureName: string = 'este recurso'): Promise<boolean> {
    const access = await this.getAccessState();

    if (access.hasAccess) {
      return true;
    }

    await this.showBlockedAlert(featureName);
    return false;
  }

  hasActivePlanAtLeast(minimumPlan: SubscriptionPlanId): boolean {
    const user = this.userService.user;
    return user?.modo_dev === true
      || (this.isPaidSubscriptionActive(user)
        && subscriptionPlanMeetsMinimum(user?.subscription_plan, minimumPlan));
  }

  async canUsePlanFeature(featureName: string, minimumPlan: SubscriptionPlanId = 'obras'): Promise<boolean> {
    await this.getAccessState();
    if (this.hasActivePlanAtLeast(minimumPlan)) return true;
    await this.showMinimumPlanAlert(featureName, minimumPlan);
    return false;
  }

  getTrialText(access: AppAccessState | null): string {
    if (!access?.isTrialActive || access.isSubscriber) {
      return '';
    }

    return access.trialDaysRemaining === 1
      ? 'Resta 1 dia de teste gratuito'
      : `Restam ${access.trialDaysRemaining} dias de teste gratuito`;
  }

  private getTrialDaysRemaining(trialEndDate: any): number {
    const trialEnd = this.parseDate(trialEndDate);

    if (!trialEnd) {
      return 0;
    }

    const diffMs = trialEnd.getTime() - Date.now();
    return Math.max(0, Math.ceil(diffMs / 86400000));
  }

  private parseDate(value: any): Date | null {
    if (!value) {
      return null;
    }

    const normalized = String(value).replace(' ', 'T');
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private parsePaidEndDate(user: any): Date | null {
    const rawDate = user?.paid_end_date_raw || user?.paid_end_date;

    if (!rawDate) {
      return null;
    }

    const rawText = String(rawDate).trim();
    const brazilianDate = rawText.match(/(\d{2})\/(\d{2})\/(\d{4})/);

    if (brazilianDate) {
      return new Date(`${brazilianDate[3]}-${brazilianDate[2]}-${brazilianDate[1]}T23:59:59`);
    }

    return this.parseDate(rawText);
  }

  private async showBlockedAlert(featureName: string) {
    if (this.alertOpen) {
      return;
    }

    this.alertOpen = true;
    const access = this.cachedAccessState || this.buildAccessState(this.userService.user);
    const subscriptionExpired = access.isSubscriptionExpired;
    const buttons: any[] = [
      {
        text: 'Agora não',
        role: 'cancel'
      },
      {
        text: subscriptionExpired ? 'Renovar' : 'Ver planos',
        handler: () => this.router.navigate(['/planos'])
      }
    ];

    const alert = await this.alertCtrl.create({
      header: subscriptionExpired ? 'Assinatura expirada' : 'Assinatura necessária',
      message: subscriptionExpired
        ? ionicSafeHtml(`
            <p>Sua assinatura ${access.subscriptionPlanName} expirou.</p>
            <p>Para continuar usando ${featureName}, renove sua assinatura.</p>
          `)
        : ionicSafeHtml(`
            <p>O teste gratuito de 7 dias terminou.</p>
            <p>Para usar ${featureName}, ative uma assinatura.</p>
          `),
      cssClass: ['custom-alert', 'alert-confirm'],
      buttons
    });

    alert.onDidDismiss().then(() => {
      this.alertOpen = false;
    });

    await alert.present();
  }

  private async showMinimumPlanAlert(featureName: string, minimumPlan: SubscriptionPlanId): Promise<void> {
    if (this.alertOpen) return;
    this.alertOpen = true;

    const user = this.userService.user;
    const currentPlan = this.isPaidSubscriptionActive(user)
      ? subscriptionPlanName(user?.subscription_plan)
      : 'Sem assinatura ativa';
    const requiredPlan = subscriptionPlanName(minimumPlan);
    const alert = await this.alertCtrl.create({
      header: `Recurso do plano ${requiredPlan}`,
      message: ionicSafeHtml(`
        <div class="plan-required-message">
          <div class="plan-required-icon"><ion-icon name="business-outline"></ion-icon></div>
          <strong>Mais controle para suas obras</strong>
          <p>${featureName} está disponível no plano ${requiredPlan} e nos planos superiores.</p>
          <div class="plan-required-current">
            <span>Seu plano atual</span>
            <b>${currentPlan}</b>
          </div>
          <small>Faça o upgrade para gerenciar acessos, equipes e os recursos avançados das suas obras.</small>
        </div>
      `),
      cssClass: ['custom-alert', 'alert-confirm', 'plan-required-alert'],
      buttons: [
        { text: 'Agora não', role: 'cancel' },
        { text: `Conhecer o ${requiredPlan}`, handler: () => this.router.navigate(['/planos']) }
      ]
    });
    alert.onDidDismiss().then(() => { this.alertOpen = false; });
    await alert.present();
  }
}
